import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import apiConfigs from '../../shared/utilities/apiConfigs';
import { api } from '../../shared/utilities/api';
import useStore from '../../shared/store';
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/auth';
import portonLogo from '../../../img/Porton_Default.png'; // confirm this path matches where LoginPage.js actually sits in your tree

// ─── Inline icons — no external font dependency.
const HeartIcon = (props) => (
	<svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15" aria-hidden="true" {...props}>
		<path d="M12 21s-7.2-4.5-9.6-9C.7 8.4 2 4.8 5.8 4.8c2 0 3.4 1 4.2 2.2.8-1.2 2.2-2.2 4.2-2.2 3.8 0 5.1 3.6 3.4 7.2C19.2 16.5 12 21 12 21z" />
	</svg>
);

const EyeIcon = (props) => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="17" height="17" aria-hidden="true" {...props}>
		<path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
		<circle cx="12" cy="12" r="3" />
	</svg>
);

const EyeOffIcon = (props) => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="17" height="17" aria-hidden="true" {...props}>
		<path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
		<line x1="1" y1="1" x2="23" y2="23" />
	</svg>
);

const ShieldIcon = (props) => (
	<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" width="11" height="11" aria-hidden="true" {...props}>
		<rect x="4" y="10" width="16" height="10" rx="2" />
		<path d="M8 10V7a4 4 0 0 1 8 0v3" />
	</svg>
);

const CheckIcon = (props) => (
	<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" width="11" height="11" aria-hidden="true" {...props}>
		<path d="M20 6 9 17l-5-5" />
	</svg>
);

const ClockIcon = (props) => (
	<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" width="11" height="11" aria-hidden="true" {...props}>
		<circle cx="12" cy="12" r="9" />
		<path d="M12 7v5l3 2" />
	</svg>
);

const FacebookIcon = (props) => (
	<svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true" {...props}>
		<path d="M14 9h2.5V6h-2.5c-2 0-3.5 1.5-3.5 3.5V11H8v3h2.5v7h3v-7h2.3l.4-3h-2.7V9.8c0-.5.3-.8.8-.8z" />
	</svg>
);

const XIcon = (props) => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" width="15" height="15" aria-hidden="true" {...props}>
		<path d="M5 5l14 14M19 5L5 19" />
	</svg>
);

const YoutubeIcon = (props) => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="17" height="17" aria-hidden="true" {...props}>
		<rect x="2.5" y="6" width="19" height="12" rx="3" />
		<polygon points="10,9.3 10,14.7 15,12" fill="currentColor" stroke="none" />
	</svg>
);

const InstagramIcon = (props) => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" width="16" height="16" aria-hidden="true" {...props}>
		<rect x="3.5" y="3.5" width="17" height="17" rx="5" />
		<circle cx="12" cy="12" r="4" />
		<circle cx="17" cy="7" r="0.8" fill="currentColor" stroke="none" />
	</svg>
);

const LinkedinIcon = (props) => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" width="16" height="16" aria-hidden="true" {...props}>
		<rect x="3.5" y="3.5" width="17" height="17" rx="3" />
		<line x1="7.5" y1="10.5" x2="7.5" y2="17" />
		<circle cx="7.5" cy="7" r="0.6" fill="currentColor" stroke="none" />
		<path d="M11 17v-4.3c0-1 .3-2.2 2-2.2s2 1.2 2 2.2V17" />
		<line x1="11" y1="11" x2="11" y2="17" />
	</svg>
);

// ─── Inline styles — self-contained, same pattern as reschedule.js.
const injectLoginStyles = () => {
	if (document.getElementById('login-page-styles')) return;
	const style = document.createElement('style');
	style.id = 'login-page-styles';
	style.textContent = `
		@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@600;700;800&family=Inter:wght@400;500;600&display=swap');

		.lp-wrap, .lp-wrap *, .lp-wrap *::before, .lp-wrap *::after {
			box-sizing: border-box;
		}

		.lp-wrap {
			--lp-brand: #DD5E22;
			--lp-brand-dark: #B94D1A;
			--lp-brand-tint: #FCEEE5;
			--lp-text: #18181A;
			--lp-text-secondary: #6B6760;
			--lp-text-tertiary: #9C978F;
			--lp-border: rgba(24,24,20,0.10);
			--lp-border-strong: rgba(24,24,20,0.18);
			min-height: 100vh;
			display: flex;
			flex-direction: column;
			font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
			color: var(--lp-text);
			background: #fff;
		}

		.lp-header { border-bottom: 1px solid var(--lp-border); }
		.lp-header__inner {
			max-width: 1200px;
			margin: 0 auto;
			padding: 18px 32px;
			display: flex;
			align-items: center;
			justify-content: space-between;
		}
		.lp-logo {
			display: flex;
			align-items: center;
			gap: 10px;
			font-family: 'Manrope', sans-serif;
			font-weight: 700;
			font-size: 16px;
			letter-spacing: -0.01em;
		}
		.lp-logo__mark {
			width: 28px;
			height: 28px;
			border-radius: 8px;
			background: radial-gradient(73.95% 73.95% at 14.58% 11.46%, #EE8F37 0%, #E47029 100%);
			color: #fff;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
		}
		.lp-nav__note { font-size: 13px; color: var(--lp-text-tertiary); }

		.lp-main { flex: 1; display: grid; grid-template-columns: 46% 54%; }

		.lp-brand {
			background: linear-gradient(155deg, #E97A3D 0%, #DD5E22 55%, #B94D1A 100%);
			position: relative;
			overflow: hidden;
			padding: 64px 56px;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			color: #fff;
		}
		.lp-brand::before {
			content: '';
			position: absolute;
			width: 420px; height: 420px;
			border-radius: 50%;
			border: 1px solid rgba(255,255,255,0.14);
			top: -140px; right: -140px;
		}
		.lp-brand::after {
			content: '';
			position: absolute;
			width: 280px; height: 280px;
			border-radius: 50%;
			border: 1px solid rgba(255,255,255,0.14);
			bottom: -100px; left: -80px;
		}
		.lp-brand__badge {
			position: relative; z-index: 1;
			display: inline-flex;
			align-items: center;
			gap: 6px;
			font-size: 11px;
			font-weight: 700;
			letter-spacing: 0.06em;
			text-transform: uppercase;
			background: rgba(255,255,255,0.16);
			padding: 6px 12px;
			border-radius: 20px;
			margin-bottom: 26px;
			width: fit-content;
		}
		.lp-brand__heading {
			position: relative; z-index: 1;
			font-family: 'Manrope', sans-serif;
			font-size: 32px;
			line-height: 1.25;
			font-weight: 700;
			margin: 0 0 14px;
			max-width: 380px;
			letter-spacing: -0.01em;
		}
		.lp-brand__sub {
			position: relative; z-index: 1;
			font-size: 15px;
			line-height: 1.6;
			color: rgba(255,255,255,0.85);
			max-width: 360px;
			margin: 0;
		}
		.lp-brand__trust {
			position: relative; z-index: 1;
			display: flex;
			flex-direction: column;
			gap: 14px;
		}
		.lp-brand__trust-item {
			display: flex;
			align-items: center;
			gap: 10px;
			font-size: 13px;
			color: rgba(255,255,255,0.92);
		}
		.lp-brand__trust-item .lp-dot {
			width: 20px; height: 20px;
			border-radius: 50%;
			background: rgba(255,255,255,0.18);
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
		}

		.lp-formside {
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 48px 32px;
		}
		.lp-card { width: 100%; max-width: 380px; }
		.lp-eyebrow {
			font-size: 12px;
			font-weight: 700;
			color: var(--lp-brand);
			text-transform: uppercase;
			letter-spacing: 0.06em;
			margin-bottom: 10px;
		}
		.lp-card h1 {
			font-family: 'Manrope', sans-serif;
			font-size: 24px;
			font-weight: 700;
			margin: 0 0 8px;
			letter-spacing: -0.01em;
		}
		.lp-lede {
			font-size: 13.5px;
			color: var(--lp-text-secondary);
			margin: 0 0 30px;
			line-height: 1.55;
		}

		.lp-field { margin-bottom: 18px; }
		.lp-field label {
			display: block;
			font-family: 'Manrope', sans-serif;
			font-size: 12px;
			font-weight: 600;
			color: var(--lp-text-secondary);
			margin-bottom: 7px;
			text-transform: uppercase;
			letter-spacing: 0.04em;
		}
		.lp-field__control { position: relative; }
		.lp-field input {
			width: 100%;
			height: 46px;
			padding: 0 14px;
			border: 1px solid var(--lp-border-strong);
			border-radius: 10px;
			font-size: 14.5px;
			font-family: 'Inter', sans-serif;
			color: var(--lp-text);
			background: #fff;
			-webkit-appearance: none;
			appearance: none;
			box-shadow: none;
			transition: border-color 0.15s ease, box-shadow 0.15s ease;
		}
		.lp-field input::placeholder { color: var(--lp-text-tertiary); }
		.lp-field input:hover { border-color: #c2bdb3; }
		.lp-field input:focus {
			outline: none;
			border-color: var(--lp-brand);
			box-shadow: 0 0 0 3px var(--lp-brand-tint);
		}
		.lp-field--icon input { padding-right: 44px; }
		.lp-field__toggle {
			position: absolute;
			right: 6px;
			top: 50%;
			transform: translateY(-50%);
			width: 32px;
			height: 32px;
			border: none;
			background: transparent;
			cursor: pointer;
			color: #6b6760;
			display: flex;
			align-items: center;
			justify-content: center;
			border-radius: 6px;
		}
		.lp-field__toggle:hover { color: var(--lp-text); background: #f3f1ee; }

		.lp-field input:-webkit-autofill,
		.lp-field input:-webkit-autofill:hover,
		.lp-field input:-webkit-autofill:focus {
			-webkit-box-shadow: 0 0 0 1000px #ffffff inset;
			-webkit-text-fill-color: var(--lp-text);
		}

		/* Chrome/Edge inject their own credentials key icon and, on some versions,
		   a native reveal-password eye into type="password" fields. Both sit in
		   the same right-side spot as our custom toggle button and collide with
		   it visually. Hide the native ones so only our button shows. */
		.lp-field input::-webkit-contacts-auto-fill-button,
		.lp-field input::-webkit-credentials-auto-fill-button {
			visibility: hidden;
			display: none !important;
			pointer-events: none;
			position: absolute;
			right: 0;
		}
		.lp-field input[type="password"]::-ms-reveal,
		.lp-field input[type="password"]::-ms-clear {
			display: none;
		}

		.lp-error {
			background: #FCEBEB;
			border: 1px solid #F2C6C6;
			color: #A32D2D;
			font-size: 12.5px;
			line-height: 1.55;
			padding: 11px 13px;
			border-radius: 10px;
			margin-bottom: 18px;
		}

		.lp-row {
			display: flex;
			align-items: center;
			justify-content: space-between;
			margin: 6px 0 26px;
		}
		.lp-checkbox {
			display: flex;
			align-items: center;
			gap: 8px;
			font-size: 13px;
			color: var(--lp-text-secondary);
		}
		.lp-checkbox input { width: 15px; height: 15px; accent-color: var(--lp-brand); }

		.lp-btn-primary {
			width: 100%;
			height: 46px;
			border: none;
			border-radius: 10px;
			background: var(--lp-brand);
			color: #fff;
			font-family: 'Manrope', sans-serif;
			font-size: 14px;
			font-weight: 700;
			letter-spacing: 0.01em;
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
		}
		.lp-btn-primary:hover:not(:disabled) { background: var(--lp-brand-dark); }
		.lp-btn-primary:active:not(:disabled) { transform: scale(0.99); }
		.lp-btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }

		.lp-spinner {
			width: 17px; height: 17px;
			border: 2px solid rgba(255,255,255,0.4);
			border-top-color: #fff;
			border-radius: 50%;
			animation: lp-spin 0.7s linear infinite;
		}
		@keyframes lp-spin { to { transform: rotate(360deg); } }

		.lp-card__foot {
			margin-top: 24px;
			text-align: center;
			font-size: 12.5px;
			color: var(--lp-text-tertiary);
		}

		/* ── Footer — same structure/links as reschedule.js ── */
		.lp-footer {
			background: #F7F2EE;
			border-top: 1px solid rgba(0,0,0,0.04);
			width: 100%;
			padding: 32px 0 22px;
			margin-top: auto;
		}
		.lp-footer__inner {
			max-width: 1200px;
			margin: 0 auto;
			padding: 0 16px;
			display: flex;
			flex-direction: column;
			align-items: center;
		}
		.lp-footer__logo {
			max-height: 50px;
			max-width: 120px;
			object-fit: contain;
			opacity: 0.75;
			margin-bottom: 16px;
		}
		.lp-footer__links {
			display: flex;
			align-items: center;
			gap: 22px;
			flex-wrap: wrap;
			justify-content: center;
			margin-bottom: 18px;
		}
		.lp-footer__links a {
			font-size: 12px;
			color: var(--lp-text-secondary);
			text-decoration: none;
		}
		.lp-footer__links a:hover { color: var(--lp-brand); }
		.lp-footer__divider {
			width: 100%;
			max-width: 400px;
			height: 1px;
			background: rgba(0,0,0,0.08);
			margin-bottom: 18px;
		}
		.lp-footer__socials {
			display: flex;
			align-items: center;
			gap: 10px;
			margin-bottom: 18px;
		}
		.lp-footer__social-icon {
			width: 34px;
			height: 34px;
			border: 1px solid var(--lp-border);
			border-radius: 8px;
			color: var(--lp-brand);
			background: #fff;
			display: flex;
			align-items: center;
			justify-content: center;
			text-decoration: none;
			transition: background 0.15s ease, border-color 0.15s ease;
		}
		.lp-footer__social-icon:hover { background: var(--lp-brand-tint); border-color: #F5D5C0; }
		.lp-footer__copyright {
			font-size: 11px;
			color: #999999;
			margin: 0;
		}

		@media (max-width: 860px) {
			.lp-main { grid-template-columns: 1fr; }
			.lp-brand { padding: 40px 28px; min-height: 200px; }
			.lp-brand__heading { font-size: 24px; }
			.lp-brand__trust { display: none; }
			.lp-formside { padding: 40px 24px; }
		}
	`;
	document.head.appendChild(style);
};

export default function LoginPage() {
	const [loading, setLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [values, setValues] = useState({ email: '', password: '' });
	const [errorMessage, setErrorMessage] = useState('');
	const [orgError, setOrgError] = useState('');

	const { setLogin, setOrg, setUser, setWhiteLabel } = useStore();
	const { whiteLabel } = useParams();

	useEffect(() => {
		injectLoginStyles();
		if (whiteLabel) {
			validOrg();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const validOrg = async () => {
		const res = await api(
			apiConfigs.baseOption('get', 'appointments/getOrgProviders', {
				params: { org_id: whiteLabel, limit: 1 },
			})
		);
		if (res.status === 200) {
			setWhiteLabel(whiteLabel);
		} else {
			setOrgError('Invalid organization or white-label URL. Please check the link and try again.');
		}
	};

	const handleFirebaseLogin = async (token) => {
		try {
			await firebase.auth().signInWithCustomToken(token);
			return true;
		} catch (error) {
			console.error(error);
			return false;
		}
	};

	const handleLogin = async (e) => {
		e.preventDefault();
		setErrorMessage('');
		setLoading(true);

		const result = await api(
			apiConfigs.baseOption('POST', 'auth/login', {
				data: { email: values.email, password: values.password },
			})
		);

		switch (result.status) {
			case 200: {
				const firebaseSuccess = await handleFirebaseLogin(result.data.firebaseToken);
				if (firebaseSuccess) {
					localStorage.setItem('token', result.data.token);
					setLogin(true);
					setOrg(result.data.default_org);
					setUser(result.data.user.document);
					setWhiteLabel(result.data.default_org.whiteLabelURL);
				} else {
					setErrorMessage('We could not verify your session. Please try again.');
				}
				break;
			}
			case 400:
				setErrorMessage('Invalid email or password. Try again.');
				break;
			default:
				setErrorMessage('Something went wrong on our end. Please try again in a moment.');
		}
		setLoading(false);
	};

	return (
		<div className="lp-wrap">
			<header className="lp-header">
				<div className="lp-header__inner">
				<img className="lp-footer__logo" src={portonLogo} alt="Powered by Porton Health" />
					<nav>
						<span className="lp-nav__note">Need help? Contact Porton</span>
					</nav>
				</div>
			</header>

			<main className="lp-main">
				<section className="lp-brand">
					<div>
						<span className="lp-brand__badge">Provider portal</span>
						<h2 className="lp-brand__heading">One place to manage every appointment, across every clinic.</h2>
						<p className="lp-brand__sub">Sign in to view your schedule, update patient records, and coordinate care with your team.</p>
					</div>
					<div className="lp-brand__trust">
						<div className="lp-brand__trust-item">
							<span className="lp-dot"><ShieldIcon /></span> 256-bit encrypted connection
						</div>
						<div className="lp-brand__trust-item">
							<span className="lp-dot"><CheckIcon /></span> PHIPA &amp; HIPAA aligned
						</div>
					</div>
				</section>

				<section className="lp-formside">
					<div className="lp-card">
						<div className="lp-eyebrow">Provider access</div>
						<h1>Welcome back</h1>
						<p className="lp-lede">Sign in with your Porton credentials to continue.</p>

						{orgError && (
							<div className="lp-error" role="alert">
								{orgError}
							</div>
						)}

						<form onSubmit={handleLogin}>
							<div className="lp-field">
								<label htmlFor="email">Email address</label>
								<div className="lp-field__control">
									<input
										id="email"
										type="email"
										placeholder="you@clinic.com"
										autoComplete="email"
										value={values.email}
										onChange={(e) => setValues({ ...values, email: e.target.value })}
										required
									/>
								</div>
							</div>

							<div className="lp-field lp-field--icon">
								<label htmlFor="password">Password</label>
								<div className="lp-field__control">
									<input
										id="password"
										type={showPassword ? 'text' : 'password'}
										placeholder="Enter password"
										autoComplete="current-password"
										value={values.password}
										onChange={(e) => setValues({ ...values, password: e.target.value })}
										required
									/>
									<button
										type="button"
										className="lp-field__toggle"
										aria-label={showPassword ? 'Hide password' : 'Show password'}
										onClick={() => setShowPassword(!showPassword)}
									>
										{showPassword ? <EyeOffIcon /> : <EyeIcon />}
									</button>
								</div>
							</div>

							{errorMessage && (
								<div className="lp-error" role="alert" aria-live="polite">
									{errorMessage}
								</div>
							)}

							<div className="lp-row">
								<label className="lp-checkbox">
									<input type="checkbox" /> Keep me signed in
								</label>
							</div>

							<button type="submit" className="lp-btn-primary" disabled={loading}>
								{loading ? <span className="lp-spinner" aria-hidden="true" /> : 'Log in'}
							</button>
						</form>

						<p className="lp-card__foot">Need a provider account? Contact your Porton administrator.</p>
					</div>
				</section>
			</main>

			<footer className="lp-footer">
				<div className="lp-footer__inner">
					<img className="lp-footer__logo" src={portonLogo} alt="Powered by Porton Health" />
					<nav className="lp-footer__links" aria-label="Footer links">
						<a href="https://www.portonhealth.com/terms-and-conditions/">
							Terms of service
						</a>
						<a href="https://www.portonhealth.com/faq/">FAQ</a>
						<a href="https://www.portonhealth.com/privacy-and-security/">
							Privacy &amp; security
						</a>
					</nav>
					<div className="lp-footer__divider"></div>
					<div className="lp-footer__socials">
						<a className="lp-footer__social-icon" href="https://www.facebook.com/people/Porton-Health/100049262388140" aria-label="Facebook">
							<FacebookIcon />
						</a>
						<a className="lp-footer__social-icon" href="https://x.com/PortonHealth" aria-label="Twitter / X">
							<XIcon />
						</a>
						<a className="lp-footer__social-icon" href="https://www.youtube.com/channel/UCZJ3tfkrBJIESr0A94vkRYg" aria-label="YouTube">
							<YoutubeIcon />
						</a>
						<a className="lp-footer__social-icon" href="https://www.instagram.com/portonhealth/?hl=en" aria-label="Instagram">
							<InstagramIcon />
						</a>
						<a className="lp-footer__social-icon" href="https://www.linkedin.com/company/porton-health/posts/?feedView=all" aria-label="LinkedIn">
							<LinkedinIcon />
						</a>
					</div>
					<p className="lp-footer__copyright">© {new Date().getFullYear()} Porton Health</p>
				</div>
			</footer>
		</div>
	);
}